/**
 * Utilities for handling @-trigger mention insertions in plain text inputs.
 * Implemented as a class with static methods to avoid duplicating logic across
 * multiple editors.
 */
export class TextInputMentions {
  private static isWhitespace(ch: string | undefined): boolean {
    return ch === undefined ? true : /\s/.test(ch)
  }

  /**
   * Returns the query immediately after the last '@' and before the caret,
   * when there is no intervening whitespace. Otherwise returns null.
   * Example: "Hello @St." with caret after ".", returns "St."
   */
  static getTriggerQuery(value: string, caret: number): string | null {
    const safe = String(value ?? '')
    const pos = Math.max(0, Math.min(caret ?? 0, safe.length))
    // Find the start of the current token by scanning back to whitespace
    let tokenStart = pos
    while (tokenStart > 0 && !this.isWhitespace(safe[tokenStart - 1])) {
      tokenStart--
    }
    // Find the last '@' within [tokenStart, pos)
    const atIndex = safe.lastIndexOf('@', pos - 1)
    // Only trigger when '@' starts the token (start-of-input or follows whitespace)
    if (atIndex !== tokenStart) return null
    return safe.slice(atIndex + 1, pos)
  }

  /**
   * Replace the @trigger word immediately before the caret with `token`.
   * Falls back to a simple caret insertion when no @trigger is found.
   *
   * Word boundaries: whitespace only (matches picker-open logic).
   */
  static replaceAtTrigger(
    element: HTMLTextAreaElement | HTMLInputElement | null,
    value: string,
    token: string
  ): { content: string; caret: number } {
    const safeValue = String(value ?? '')
    const pos = element?.selectionStart ?? safeValue.length
    const end = element?.selectionEnd ?? pos
    // Determine token start by whitespace-only scan and ensure '@' exists
    let tokenStart = pos
    while (tokenStart > 0 && !this.isWhitespace(safeValue[tokenStart - 1])) {
      tokenStart--
    }
    const atIndex = safeValue.lastIndexOf('@', pos - 1)
    // Only replace when '@' starts the token
    if (atIndex !== tokenStart) {
      // No @trigger — simple insertion at caret
      const content =
        safeValue.slice(0, pos) + token + safeValue.slice(end, safeValue.length)
      return { content, caret: pos + token.length }
    }

    // Delete from the '@' through the caret (leave any trailing text after caret intact)
    const content =
      safeValue.slice(0, atIndex) + token + safeValue.slice(end, safeValue.length)
    const caret = atIndex + token.length
    return { content, caret }
  }
}

